import * as React from "react";
import { ChevronDown, Info } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Collapsible,
  CollapsibleContent,
} from "@/components/ui/collapsible";
import { cn } from "@/lib/utils";
import {
  REGIME_OVERLAY_RESEARCH_DISCLAIMER_EXPANDED,
  RESEARCH_DISCLAIMER_COLLAPSED,
  SIMULATOR_RESEARCH_DISCLAIMER_EXPANDED,
  STRATEGY_RD_RESEARCH_DISCLAIMER_EXPANDED,
  type ResearchDisclaimerKind,
} from "../constants/researchDisclaimers";
import {
  readResearchDisclaimerExpanded,
  writeResearchDisclaimerExpanded,
} from "../utils/researchDisclaimerUiPersistence";

const EXPANDED_BY_KIND: Record<ResearchDisclaimerKind, string> = {
  simulator: SIMULATOR_RESEARCH_DISCLAIMER_EXPANDED,
  regime: REGIME_OVERLAY_RESEARCH_DISCLAIMER_EXPANDED,
  strategy_rd: STRATEGY_RD_RESEARCH_DISCLAIMER_EXPANDED,
};

const DISCLAIMER_BODY_CLASS =
  "font-[family-name:var(--font-primary)] text-sm font-normal leading-5 text-gray-500 dark:text-white";

export type ResearchDisclaimerNoticeProps = {
  kind: ResearchDisclaimerKind;
  className?: string;
  /** Optional override for tests / special surfaces. */
  expandedBody?: string;
};

type DisclaimerToggleButtonProps = {
  expanded: boolean;
  onClick: () => void;
  className?: string;
};

function DisclaimerToggleButton({
  expanded,
  onClick,
  className,
}: DisclaimerToggleButtonProps): React.ReactElement {
  return (
    <Button
      type="button"
      variant="link"
      size="sm"
      data-font="primary"
      data-research-disclaimer-toggle=""
      aria-expanded={expanded}
      aria-label={expanded ? "Show less disclaimer" : "Show more disclaimer"}
      className={cn(
        "h-5 min-h-0 shrink-0 items-center gap-1 whitespace-nowrap p-0 leading-5 no-underline hover:bg-transparent hover:no-underline",
        className
      )}
      style={{
        paddingInline: 0,
        paddingBlock: 0,
        height: "auto",
        color: "var(--brand-tropical-surf)",
      }}
      onClick={onClick}
    >
      <span className="normal-case leading-5 tracking-normal">
        {expanded ? "Show less" : "Show more"}
      </span>
      <ChevronDown
        aria-hidden
        data-icon="inline-end"
        className={cn(
          "!size-3.5 !translate-y-0 shrink-0 text-[color:var(--brand-tropical-surf)] transition-transform duration-200",
          expanded && "rotate-180"
        )}
      />
    </Button>
  );
}

/**
 * Always-visible research disclaimer. Collapse remembers preference in localStorage
 * per kind; collapsed state still shows the short one-liner (never fully dismissable).
 *
 * Layout: one flex row of icon, text and toggle. Collapsed, the row is vertically centred
 * and the one-liner truncates to a single line; expanded, the row aligns to the top so the
 * icon and toggle sit on the first line of the full text.
 */
export function ResearchDisclaimerNotice({
  kind,
  className,
  expandedBody,
}: ResearchDisclaimerNoticeProps): React.ReactElement {
  const [expanded, setExpanded] = React.useState(() =>
    readResearchDisclaimerExpanded(kind)
  );

  const body = expandedBody ?? EXPANDED_BY_KIND[kind];

  const onOpenChange = React.useCallback(
    (next: boolean) => {
      setExpanded(next);
      writeResearchDisclaimerExpanded(kind, next);
    },
    [kind]
  );

  const toggle = () => onOpenChange(!expanded);

  return (
    <Alert
      variant="info"
      data-research-disclaimer={kind}
      data-research-disclaimer-expanded={expanded ? "" : undefined}
      className={cn(
        "flex w-full gap-3 rounded-lg px-4 py-3 font-[family-name:var(--font-primary)]",
        expanded ? "items-start" : "items-center",
        className
      )}
    >
      {/* Wrapped so the Alert's direct-child svg offsets (translate / row-span) don't apply. */}
      <span aria-hidden className="flex h-5 w-4 shrink-0 items-center justify-center">
        <Info className="size-4 text-[color:var(--brand-tropical-surf)]" />
      </span>

      <Collapsible
        open={expanded}
        onOpenChange={onOpenChange}
        className="min-w-0 flex-1"
      >
        {!expanded ? (
          <AlertDescription
            className={cn(DISCLAIMER_BODY_CLASS, "block truncate")}
            title={RESEARCH_DISCLAIMER_COLLAPSED}
          >
            {RESEARCH_DISCLAIMER_COLLAPSED}
          </AlertDescription>
        ) : (
          <CollapsibleContent>
            <AlertDescription className={DISCLAIMER_BODY_CLASS}>{body}</AlertDescription>
          </CollapsibleContent>
        )}
      </Collapsible>

      <DisclaimerToggleButton expanded={expanded} onClick={toggle} />
    </Alert>
  );
}
